/**
 * @docs docs/features/website-navigation-shell
 * @adr  ADR-0110
 */

/*----------------------------------
- WORLD FOOTER
----------------------------------*/

import type { ComponentChildren } from 'preact';

import { IconButton } from '@uniquedomains/components/kit/icon-button/IconButton';
import { TextLink } from '@uniquedomains/components/kit/text-link/TextLink';
import { ProofLine } from '@uniquedomains/components/marketing/proof-line/ProofLine';
import { WorldPicture, type WorldPictureSources } from '@uniquedomains/components/marketing/hero-scene/WorldPicture';
import WordmarkWhite from '@/client/assets/identity/wordmark-white.png';
import Icon from '@/client/components/ui/icon/Icon';
import { WEBSITE_SOCIAL_LINKS } from '@/client/catalogs/landing/socialLinks';
import { WORLD_FOOTER_COPY } from '@/client/catalogs/landing/worldFooter';
import { formatMillionsStat } from '@/client/pages/landing/lib/formatCompactStat';

/*----------------------------------
- TYPES
----------------------------------*/

/** The two server figures the sky reads, when the page's data carries them (Home's `stats` today). */
export type WorldFooterStats = {
    totalDomains: number;
    totalSignups: number;
};

type WorldFooterProps = {
    stats?: WorldFooterStats;
    /** The night launch picture (`WORLD_FOOTER_NIGHT_SCENE`), always lazy: it sits far below the fold on every page. */
    sources: WorldPictureSources;
    /** The two persona cards (`.pcards`) of every page, landing on the picture's sand above the plan line, as the mock draws them. */
    personaCards?: ComponentChildren;
    brandHref: string;
    signIn: { href: string; testId: string; onClick: () => void };
    githubHref: string;
    /** The six link groups (`FooterColumn`), in the shipped order. */
    children: ComponentChildren;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * The page's foot as the mock draws it (`footer.world.wf#solutions`, Home v3's night launch, which the page template
 * carries to every page since 2026-11-04): a rounded card at the hero card's insets; the closing words in the night sky
 * (`.wf-sky`), then the launch picture faded into that sky and into its own sand (`.wf-scene`, lazy), then the two persona
 * cards, the plan line and the sign-in on the sand (`.wf-paths`), then the link groups, the bottom bar and the big white
 * wordmark on the sand darkening below (`.wf-ground`). Presentational: `Footer` owns the catalogs and the tracking. The
 * `solutions` id moved here from Home's old closing section (batch 7, C4), as the mock's template draws it on every page:
 * one footer a page, so the id stays unique. The mock's reveal (`.rv`) is not drawn: the frame has no reveal script
 * outside Home, and the footer's words show at once.
 */
export default function WorldFooter({ stats, sources, personaCards, brandHref, signIn, githubHref, children }: WorldFooterProps) {
    // A missing or sub-million figure drops to the catalog's wording ("Millions of"): never a zero, never a typed count.
    const domainsFigure = (stats ? formatMillionsStat(stats.totalDomains) : null) ?? WORLD_FOOTER_COPY.headlineCountFallback;
    const currentYear = new Date().getFullYear();

    return (
        <footer class="world wf" id="solutions">
            <div class="wf-card">
                <div class="wf-sky">
                    <div class="wrap w-cta wf-cta">
                        <h2>
                            <span class="y">{domainsFigure}</span> {WORLD_FOOTER_COPY.headlineLead}{' '}
                            <span class="dim">{WORLD_FOOTER_COPY.headlineFollowUp}</span>
                        </h2>
                        <p class="sub">{WORLD_FOOTER_COPY.sub}</p>
                        {stats && stats.totalSignups > 0 ? <ProofLine count={stats.totalSignups} label={WORLD_FOOTER_COPY.proofLabel} /> : null}
                    </div>
                </div>

                {/* Decorative: the picture carries no information (`WorldPicture` draws `alt=""`). */}
                <div class="wf-scene" aria-hidden="true">
                    <WorldPicture sources={sources} priority="lazy" />
                </div>

                <div class="wrap wf-paths">
                    {personaCards ? <div class="pcards">{personaCards}</div> : null}
                    <p class="reass">{WORLD_FOOTER_COPY.reassurance.join('  ·  ')}</p>
                    <TextLink class="signin" size="lg" onInk href={signIn.href} onClick={signIn.onClick} data-testid={signIn.testId}>
                        {WORLD_FOOTER_COPY.signInLabel}
                    </TextLink>
                </div>

                <div class="w-ground wf-ground">
                    <div class="wrap w-links">{children}</div>

                    <div class="wrap w-bottom">
                        {/* The mock's logo, drawn in CSS (`.logo`) until a white lockup asset exists. The link's name is the
                            wordmark it shows ("unique.domains"), not an `aria-label`: a label that does not hold the visible
                            text fails WCAG 2.5.3 (Lighthouse `label-content-name-mismatch`, batch 7 review M6). */}
                        <a class="logo" href={brandHref}>
                            <i aria-hidden="true">u.</i>
                            <span class="wm">
                                unique<em>.</em>domains
                            </span>
                        </a>
                        <span>
                            © {currentYear} {WORLD_FOOTER_COPY.companyName}
                        </span>
                        <div class="soc">
                            {WEBSITE_SOCIAL_LINKS.map(({ label, href, icon }) => (
                                <IconButton
                                    key={label}
                                    variant="on-ink"
                                    size="lg"
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    label={label}
                                    icon={<Icon name={icon} size={16} strokeWidth={1.9} />}
                                />
                            ))}
                            <IconButton
                                variant="on-ink"
                                size="lg"
                                href={githubHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                label="GitHub"
                                icon={<Icon name="github" size={16} strokeWidth={1.9} />}
                            />
                        </div>
                    </div>

                    {/* Intrinsic size reserves the box, so the lazy wordmark cannot shift the page. */}
                    <div class="wrap">
                        <img class="w-mark" src={WordmarkWhite} alt="" aria-hidden="true" width="1698" height="256" loading="lazy" decoding="async" />
                    </div>
                </div>
            </div>
        </footer>
    );
}
